import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  area as d3Area,
  curveMonotoneX,
  curveMonotoneY,
  line as d3Line,
  scaleBand,
} from "d3";

import { formatTemporalCategoryTooltip } from "@/lib/weekDates";
import { cn } from "@/lib/utils";

import { ChartLegend, type LegendItem } from "./ChartLegend";
import { CartesianAxes } from "./ChartAxis";
import { ChartTooltip, type TooltipRow } from "./ChartTooltip";
import { carrierCodeForLegendEntry } from "./carrierLegend";
import { seriesColor, useChartTheme } from "./theme";
import type {
  CartesianChartSpec,
  ChartInteraction,
  ChartMask,
  ChartVariant,
} from "./types";
import {
  formatSeriesValue,
  isStackedBarChart,
  seriesYAxis,
  useCartesianModel,
} from "./useChartModel";

interface CartesianChartProps extends ChartInteraction {
  spec: CartesianChartSpec;
  variant: ChartVariant;
  mask?: ChartMask;
}

type ResolvedType = "bar" | "line";

function resolveType(
  specType: CartesianChartSpec["type"],
  seriesType: "bar" | "line" | undefined,
): ResolvedType {
  if (specType === "bar") return "bar";
  if (specType === "line") return "line";
  return seriesType ?? "bar";
}

function seriesLabel(
  title: string | undefined,
  index: number,
  seriesCount: number,
): string {
  const trimmed = title?.trim();
  if (trimmed) return trimmed;
  return seriesCount > 1 ? `Series ${index + 1}` : "Value";
}

function seriesFocusClass(
  index: number,
  hoveredSeries: number | null,
): string {
  return hoveredSeries != null && hoveredSeries !== index
    ? "chart-series-focus is-dimmed"
    : "chart-series-focus";
}

/** Overlap adjacent stack rects so anti-aliased seams do not show through. */
const STACK_SEGMENT_OVERLAP = 1;

interface StackedSegment {
  seriesIndex: number;
  value: number;
  bottom: number;
  top: number;
}

/** Rounded rect path with per-corner control (SVG `<rect rx>` rounds all four). */
function roundedRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radii: { tl: number; tr: number; br: number; bl: number },
): string {
  if (width <= 0 || height <= 0) return "";
  const rtl = Math.min(radii.tl, width / 2, height / 2);
  const rtr = Math.min(radii.tr, width / 2, height / 2);
  const rbr = Math.min(radii.br, width / 2, height / 2);
  const rbl = Math.min(radii.bl, width / 2, height / 2);
  const right = x + width;
  const bottom = y + height;

  return [
    `M ${x + rtl} ${y}`,
    `H ${right - rtr}`,
    rtr > 0
      ? `A ${rtr} ${rtr} 0 0 1 ${right} ${y + rtr}`
      : `L ${right} ${y}`,
    `V ${bottom - rbr}`,
    rbr > 0
      ? `A ${rbr} ${rbr} 0 0 1 ${right - rbr} ${bottom}`
      : `L ${right} ${bottom}`,
    `H ${x + rbl}`,
    rbl > 0
      ? `A ${rbl} ${rbl} 0 0 1 ${x} ${bottom - rbl}`
      : `L ${x} ${bottom}`,
    `V ${y + rtl}`,
    rtl > 0
      ? `A ${rtl} ${rtl} 0 0 1 ${x + rtl} ${y}`
      : `L ${x} ${y}`,
    "Z",
  ].join(" ");
}

function stackedSegmentsForCategory(
  category: string,
  series: CartesianChartSpec["series"],
  resolved: ResolvedType[],
  valueMaps: Map<string, number>[],
  logY: boolean,
): StackedSegment[] {
  const segments: StackedSegment[] = [];
  let cumulative = 0;
  series.forEach((_, i) => {
    if (resolved[i] !== "bar") return;
    const value = valueMaps[i].get(category) ?? 0;
    if (!Number.isFinite(value) || value <= 0) return;
    if (logY && !(value > 0)) return;
    segments.push({
      seriesIndex: i,
      value,
      bottom: cumulative,
      top: cumulative + value,
    });
    cumulative += value;
  });
  return segments;
}

/**
 * Bar / line / mixed renderer. D3 computes the scales and the line generator;
 * React renders the SVG marks so theme + hover stay in the React tree.
 */
export function CartesianChart({
  spec,
  variant,
  mask,
  onCategoryClick,
  onSeriesClick,
  selectedCategory = null,
  selectedSeriesKey = null,
  replayNonce = 0,
}: CartesianChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const clipNs = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const theme = useChartTheme();
  const [active, setActive] = useState<number | null>(null);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const [hoveredSeries, setHoveredSeries] = useState<number | null>(null);
  const [selectedSeries, setSelectedSeries] = useState<number | null>(null);
  const [skipEnter, setSkipEnter] = useState(false);
  const [enterNonce, setEnterNonce] = useState(0);

  const seriesCount = spec.series.length;
  useEffect(() => {
    setSelectedSeries((cur) => (cur != null && cur >= seriesCount ? null : cur));
    setHoveredSeries(null);
    setSkipEnter(false);
    setEnterNonce(0);
  }, [seriesCount]);

  const markNonce = replayNonce > 0 ? replayNonce : enterNonce;
  const replaying = replayNonce > 0 || skipEnter;

  const isolatedSeries = useMemo(() => {
    if (onSeriesClick != null) {
      if (selectedSeriesKey == null) return null;
      const idx = spec.series.findIndex(
        (s) => s.key === selectedSeriesKey || s.title === selectedSeriesKey,
      );
      return idx >= 0 ? idx : null;
    }
    return selectedSeries;
  }, [onSeriesClick, selectedSeries, selectedSeriesKey, spec.series]);

  const model = useCartesianModel(spec, variant, ref, isolatedSeries);

  const {
    margin,
    innerWidth,
    innerHeight,
    x,
    y,
    yBaseline,
    logY,
    categories,
    horizontal,
    yBand,
    xValue,
    xValueBaseline,
  } = model;
  const valueScaleFor = (seriesIndex: number) => {
    if (horizontal && xValue) return xValue;
    return seriesYAxis(spec.series[seriesIndex]) === "right" && model.y2
      ? model.y2
      : y;
  };
  const baselineFor = (seriesIndex: number) => {
    if (horizontal && xValueBaseline != null) return xValueBaseline;
    return seriesYAxis(spec.series[seriesIndex]) === "right" &&
      model.y2Baseline != null
      ? model.y2Baseline
      : yBaseline;
  };
  const y2Title = spec.type === "mixed" ? spec.y2Title : undefined;
  const gradientArea =
    (spec.type === "line" || spec.type === "mixed") && spec.gradientArea === true;

  const resolved = spec.series.map((s) => resolveType(spec.type, s.type));
  const lineSeriesCount = resolved.filter((t) => t === "line").length;
  const barIndexes = resolved
    .map((t, i) => (t === "bar" ? i : -1))
    .filter((i) => i >= 0)
    .filter((i) => isolatedSeries == null || isolatedSeries === i);

  const categoryBandwidth = horizontal
    ? (yBand?.bandwidth() ?? 0)
    : x.bandwidth();
  const groupBand = scaleBand<number>()
    .domain(barIndexes)
    .range([0, categoryBandwidth])
    .padding(0.08);

  // Per-series lookup of category -> value for O(1) mark + tooltip access.
  const valueMaps = useMemo(
    () =>
      spec.series.map((s) => {
        const map = new Map<string, number>();
        for (const point of s.data) map.set(point.x, point.y);
        return map;
      }),
    [spec.series],
  );

  const lineGen = horizontal
    ? d3Line<string>()
        .y((cat) => (yBand?.(cat) ?? 0) + categoryBandwidth / 2)
        .curve(curveMonotoneY)
    : d3Line<string>()
        .x((cat) => (x(cat) ?? 0) + categoryBandwidth / 2)
        .curve(curveMonotoneX);

  // Continuous cursor tracking across the whole plot (including the gaps
  // between bars) so the nearest category is always highlighted and the
  // tooltip follows the pointer.
  const categoryIndexAt = (event: ReactMouseEvent<SVGRectElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || categories.length === 0) return null;
    if (horizontal) {
      if (innerHeight <= 0) return null;
      const localY = event.clientY - rect.top - margin.top;
      const step = innerHeight / categories.length;
      const idx = Math.max(
        0,
        Math.min(categories.length - 1, Math.floor(localY / step)),
      );
      return { idx, x: event.clientX, y: event.clientY };
    }
    if (innerWidth <= 0) return null;
    const localX = event.clientX - rect.left - margin.left;
    const step = innerWidth / categories.length;
    const idx = Math.max(
      0,
      Math.min(categories.length - 1, Math.floor(localX / step)),
    );
    return { idx, x: event.clientX, y: event.clientY };
  };

  const handleMove = (event: ReactMouseEvent<SVGRectElement>) => {
    const hit = categoryIndexAt(event);
    if (!hit) return;
    setActive(hit.idx);
    setPointer({ x: hit.x, y: hit.y });
  };

  const handleLeave = () => {
    setActive(null);
    setPointer(null);
  };

  const handleClick = (event: ReactMouseEvent<SVGRectElement>) => {
    if (!onCategoryClick) return;
    const hit = categoryIndexAt(event);
    if (!hit) return;
    const cat = categories[hit.idx];
    const point = spec.series[0]?.data.find((p) => p.x === cat);
    onCategoryClick({
      category: cat,
      categoryKey: point?.key ?? cat,
    });
  };

  const toggleSeries = (index: number) => {
    if (onSeriesClick) {
      const s = spec.series[index];
      onSeriesClick({
        title: seriesLabel(s.title, index, spec.series.length),
        seriesKey: s.key ?? s.title ?? "",
        index,
      });
      return;
    }
    setSkipEnter(true);
    setEnterNonce((n) => n + 1);
    setSelectedSeries((cur) => (cur === index ? null : index));
  };

  const bandwidth = categoryBandwidth;
  const stackedBars =
    isStackedBarChart(spec) && isolatedSeries == null && barIndexes.length > 1;
  const stackBarThickness = bandwidth;

  // Series are ranked largest-first (stack base / first cluster). Keep that
  // order in the legend so the most influential series reads first.
  const seriesDisplayOrder = spec.series.map((_, i) => i);

  const carrierLegend =
    spec.type === "bar" || spec.type === "line" || spec.type === "mixed"
      ? spec.carrierLegend
      : undefined;

  const legendItems: LegendItem[] = seriesDisplayOrder.map((i) => {
    const s = spec.series[i];
    const label = seriesLabel(s.title, i, spec.series.length);
    return {
      label,
      color: seriesColor(theme.series, i),
      carrierCode: carrierCodeForLegendEntry(
        carrierLegend,
        "series",
        s.key ?? s.title,
        label,
      ),
    };
  });

  const activeCat = active != null ? categories[active] : null;
  const focusedSeries = isolatedSeries ?? hoveredSeries;
  const selectedCat =
    selectedCategory != null && categories.includes(selectedCategory)
      ? selectedCategory
      : null;
  const tooltipRows: TooltipRow[] =
    activeCat != null
      ? seriesDisplayOrder
          .flatMap((i) => {
            if (focusedSeries != null && focusedSeries !== i) return [];
            const s = spec.series[i];
            const raw = valueMaps[i].get(activeCat);
            if (raw == null) return [];
            return [
              {
                label: seriesLabel(s.title, i, spec.series.length),
                color: seriesColor(theme.series, i),
                value: formatSeriesValue(raw, spec.series[i].valueFormat),
                sortValue: raw,
              },
            ];
          })
          .sort((a, b) => b.sortValue - a.sortValue || a.label.localeCompare(b.label))
          .map(({ label, color, value }) => ({ label, color, value }))
      : [];

  const legendIndexForSeries = (seriesIndex: number | null) =>
    seriesIndex == null ? null : seriesDisplayOrder.indexOf(seriesIndex);

  return (
    <div
      className={cn(
        "flex h-full min-h-0 w-full flex-col",
        replaying ? "chart-static" : undefined,
        horizontal ? "chart-orient-horizontal" : undefined,
      )}
    >
      <div ref={ref} className="relative min-h-0 w-full flex-1">
        <svg
          width={model.width}
          height={model.height}
          role="img"
          aria-label={spec.title ?? `${spec.type} chart`}
          className="block"
        >
        {gradientArea && (
          <defs>
            {spec.series.map((_, i) => {
              if (resolved[i] !== "line") return null;
              const stroke = seriesColor(theme.series, i);
              const peak = lineSeriesCount > 1 ? 0.26 : 0.38;
              return horizontal ? (
                <linearGradient
                  key={`area-grad-${i}`}
                  id={`${clipNs}-line-area-${i}`}
                  x1="1"
                  y1="0"
                  x2="0"
                  y2="0"
                >
                  <stop offset="0%" stopColor={stroke} stopOpacity={peak} />
                  <stop offset="50%" stopColor={stroke} stopOpacity={peak * 0.35} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              ) : (
                <linearGradient
                  key={`area-grad-${i}`}
                  id={`${clipNs}-line-area-${i}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={stroke} stopOpacity={peak} />
                  <stop offset="55%" stopColor={stroke} stopOpacity={peak * 0.32} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              );
            })}
          </defs>
        )}
        <g transform={`translate(${margin.left},${margin.top})`}>
          <CartesianAxes
            model={model}
            theme={theme}
            xTitle={spec.xTitle}
            yTitle={spec.yTitle}
            y2Title={y2Title}
            maskCategory={mask?.category}
            maskValue={mask?.value}
          />

          {/* Selected + hover category highlight. */}
          {selectedCat != null && !horizontal && (
            <rect
              x={x(selectedCat) ?? 0}
              y={0}
              width={bandwidth}
              height={innerHeight}
              fill={theme.axis}
              opacity={0.14}
              rx={4}
            />
          )}
          {activeCat != null && activeCat !== selectedCat && !horizontal && (
            <rect
              x={x(activeCat) ?? 0}
              y={0}
              width={bandwidth}
              height={innerHeight}
              fill={theme.axis}
              opacity={0.06}
              rx={4}
            />
          )}
          {selectedCat != null && horizontal && yBand && (
            <rect
              x={0}
              y={yBand(selectedCat) ?? 0}
              width={innerWidth}
              height={bandwidth}
              fill={theme.axis}
              opacity={0.14}
              rx={4}
            />
          )}
          {activeCat != null && activeCat !== selectedCat && horizontal && yBand && (
            <rect
              x={0}
              y={yBand(activeCat) ?? 0}
              width={innerWidth}
              height={bandwidth}
              fill={theme.axis}
              opacity={0.06}
              rx={4}
            />
          )}

          {/* Bars — stacked or grouped per category. */}
          {stackedBars
            ? categories.map((cat, di) => {
                const segments = stackedSegmentsForCategory(
                  cat,
                  spec.series,
                  resolved,
                  valueMaps,
                  logY,
                );
                if (segments.length === 0) return null;

                if (horizontal && yBand && xValue && xValueBaseline != null) {
                  const barY = yBand(cat) ?? 0;
                  const stackLeft = xValue(segments[0].bottom);
                  const stackRight = xValue(segments[segments.length - 1].top);
                  const stackWidth = Math.max(0, stackRight - stackLeft);
                  if (stackWidth < 0.5) return null;
                  const rx = Math.min(3, stackBarThickness / 2, stackWidth / 2);
                  const clipPathId = `${clipNs}-stack-${di}`;
                  const clipD = roundedRectPath(
                    stackLeft,
                    barY,
                    stackWidth,
                    stackBarThickness,
                    { tl: 0, tr: rx, br: rx, bl: 0 },
                  );
                  if (!clipD) return null;

                  return (
                    <g
                      key={`stack-${cat}-${markNonce}`}
                      className="chart-bar"
                      style={{ "--bar-index": di } as CSSProperties}
                    >
                      <defs>
                        <clipPath id={clipPathId}>
                          <path d={clipD} />
                        </clipPath>
                      </defs>
                      <g clipPath={`url(#${clipPathId})`}>
                        {segments.map((seg, segIdx) => {
                          const color = seriesColor(theme.series, seg.seriesIndex);
                          const segLeftPx = xValue(seg.bottom);
                          const segRightPx = xValue(seg.top);
                          const overlap =
                            segIdx > 0 ? STACK_SEGMENT_OVERLAP : 0;
                          const width = Math.max(
                            0,
                            segRightPx - segLeftPx + overlap,
                          );
                          if (width < 0.5) return null;
                          return (
                            <rect
                              key={`${cat}-${seg.seriesIndex}`}
                              x={segLeftPx}
                              y={barY}
                              width={width}
                              height={stackBarThickness}
                              fill={color}
                              opacity={
                                selectedCat != null && cat !== selectedCat
                                  ? 0.28
                                  : hoveredSeries != null &&
                                      hoveredSeries !== seg.seriesIndex
                                    ? 0.14
                                    : 1
                              }
                            />
                          );
                        })}
                      </g>
                    </g>
                  );
                }

                const barX = x(cat) ?? 0;
                const stackTop = y(segments[segments.length - 1].top);
                const stackBottom = y(segments[0].bottom);
                const stackHeight = Math.max(0, stackBottom - stackTop);
                if (stackHeight < 0.5) return null;
                const rx = Math.min(3, stackBarThickness / 2, stackHeight / 2);
                const clipPathId = `${clipNs}-stack-${di}`;
                const clipD = roundedRectPath(
                  barX,
                  stackTop,
                  stackBarThickness,
                  stackHeight,
                  { tl: rx, tr: rx, br: 0, bl: 0 },
                );
                if (!clipD) return null;

                return (
                  <g
                    key={`stack-${cat}-${markNonce}`}
                    className="chart-bar"
                    style={{ "--bar-index": di } as CSSProperties}
                  >
                    <defs>
                      <clipPath id={clipPathId}>
                        <path d={clipD} />
                      </clipPath>
                    </defs>
                    <g clipPath={`url(#${clipPathId})`}>
                      {segments.map((seg, segIdx) => {
                        const color = seriesColor(theme.series, seg.seriesIndex);
                        const segTopPx = y(seg.top);
                        const segBottomPx = y(seg.bottom);
                        const overlap = segIdx > 0 ? STACK_SEGMENT_OVERLAP : 0;
                        const height = Math.max(
                          0,
                          segBottomPx - segTopPx + overlap,
                        );
                        if (height < 0.5) return null;
                        return (
                          <rect
                            key={`${cat}-${seg.seriesIndex}`}
                            x={barX}
                            y={segTopPx}
                            width={stackBarThickness}
                            height={height}
                            fill={color}
                            opacity={
                              selectedCat != null && cat !== selectedCat
                                ? 0.28
                                : hoveredSeries != null &&
                                    hoveredSeries !== seg.seriesIndex
                                  ? 0.14
                                  : 1
                            }
                          />
                        );
                      })}
                    </g>
                  </g>
                );
              })
            : spec.series.map((s, i) => {
            if (resolved[i] !== "bar") return null;
            if (isolatedSeries != null && isolatedSeries !== i) return null;
            const color = seriesColor(theme.series, i);
            const groupThickness = groupBand.bandwidth();
            return (
              <g
                key={`bars-${i}-${markNonce}`}
                className={seriesFocusClass(i, hoveredSeries)}
              >
                {s.data.map((point, di) => {
                  if (logY && !(point.y > 0)) return null;
                  const seriesValue = valueScaleFor(i);
                  const seriesBaseline = baselineFor(i);
                  const valuePx = seriesValue(point.y);
                  if (!Number.isFinite(valuePx)) return null;
                  const extent = Math.abs(valuePx - seriesBaseline);
                  if (extent < 0.5) return null;

                  if (horizontal && yBand) {
                    const catY = yBand(point.x);
                    if (catY == null) return null;
                    const by = catY + (groupBand(i) ?? 0);
                    const left = Math.min(seriesBaseline, valuePx);
                    return (
                      <rect
                        key={point.x}
                        className="chart-bar"
                        style={{ "--bar-index": di } as CSSProperties}
                        x={left}
                        y={by}
                        width={extent}
                        height={groupThickness}
                        rx={Math.min(3, groupThickness / 2)}
                        fill={color}
                        opacity={
                          selectedCat != null && point.x !== selectedCat
                            ? 0.28
                            : 1
                        }
                      />
                    );
                  }

                  const catX = x(point.x);
                  if (catX == null) return null;
                  const top = Math.min(seriesBaseline, valuePx);
                  const bx = catX + (groupBand(i) ?? 0);
                  return (
                    <rect
                      key={point.x}
                      className="chart-bar"
                      style={{ "--bar-index": di } as CSSProperties}
                      x={bx}
                      y={top}
                      width={groupThickness}
                      height={extent}
                      rx={Math.min(3, groupThickness / 2)}
                      fill={color}
                      opacity={
                        selectedCat != null && point.x !== selectedCat ? 0.28 : 1
                      }
                    />
                  );
                })}
              </g>
            );
          })}

          {/* Lines. */}
          {spec.series.map((_, i) => {
            if (resolved[i] !== "line") return null;
            if (isolatedSeries != null && isolatedSeries !== i) return null;
            const color = seriesColor(theme.series, i);
            const seriesValue = valueScaleFor(i);
            const lineDefined = (cat: string) => {
              const v = valueMaps[i].get(cat);
              return v != null && (!logY || v > 0);
            };
            const path =
              horizontal
                ? lineGen
                    .defined(lineDefined)
                    .x((cat) => seriesValue(valueMaps[i].get(cat) as number))(
                    categories,
                  ) ?? ""
                : lineGen
                    .defined(lineDefined)
                    .y((cat) => seriesValue(valueMaps[i].get(cat) as number))(
                    categories,
                  ) ?? "";
            const seriesBaseline = baselineFor(i);
            const areaPath = gradientArea
              ? horizontal
                ? (d3Area<string>()
                    .defined(lineDefined)
                    .y(
                      (cat) =>
                        (yBand?.(cat) ?? 0) + categoryBandwidth / 2,
                    )
                    .x0(seriesBaseline)
                    .x1((cat) => seriesValue(valueMaps[i].get(cat) as number))
                    .curve(curveMonotoneY)(categories) ?? "")
                : (d3Area<string>()
                    .defined(lineDefined)
                    .x((cat) => (x(cat) ?? 0) + bandwidth / 2)
                    .y0(seriesBaseline)
                    .y1((cat) => seriesValue(valueMaps[i].get(cat) as number))
                    .curve(curveMonotoneX)(categories) ?? "")
              : "";
            return (
              <g
                key={`line-${i}-${markNonce}`}
                style={{ "--series-index": i } as CSSProperties}
              >
                <g className={seriesFocusClass(i, hoveredSeries)}>
                  {gradientArea && areaPath && (
                    <path
                      className="chart-line-area"
                      d={areaPath}
                      fill={`url(#${clipNs}-line-area-${i})`}
                      stroke="none"
                      opacity={
                        selectedCat != null
                          ? 0.45
                          : hoveredSeries != null && hoveredSeries !== i
                            ? 0.2
                            : 1
                      }
                    />
                  )}
                  <path
                    className="chart-line"
                    d={path}
                    pathLength={1}
                    fill="none"
                    stroke={color}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {categories.map((cat, di) => {
                    const val = valueMaps[i].get(cat);
                    if (val == null) return null;
                    if (logY && !(val > 0)) return null;
                    const valuePx = seriesValue(val);
                    if (!Number.isFinite(valuePx)) return null;
                    const cx = horizontal
                      ? valuePx
                      : (x(cat) ?? 0) + bandwidth / 2;
                    const cy = horizontal
                      ? (yBand?.(cat) ?? 0) + bandwidth / 2
                      : valuePx;
                    return (
                      <circle
                        key={cat}
                        className="chart-point"
                        style={{ "--point-index": di } as CSSProperties}
                        cx={cx}
                        cy={cy}
                        r={
                          (active != null && categories[active] === cat) ||
                          cat === selectedCat
                            ? 3.5
                            : 2
                        }
                        fill={color}
                        opacity={
                          selectedCat != null && cat !== selectedCat ? 0.28 : 1
                        }
                      />
                    );
                  })}
                </g>
              </g>
            );
          })}

          {/* Full-plot hover surface for continuous cursor tracking. */}
          <rect
            x={0}
            y={0}
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            style={onCategoryClick ? { cursor: "pointer" } : undefined}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            onClick={handleClick}
          />
        </g>
      </svg>

      {activeCat != null && pointer && innerWidth > 0 && (
        <ChartTooltip
          anchorX={pointer.x}
          anchorY={pointer.y}
          title={formatTemporalCategoryTooltip(activeCat)}
          rows={tooltipRows}
          mask={mask}
          hint={
            onCategoryClick
              ? activeCat === selectedCat
                ? "Click to clear filter"
                : "Click to filter other charts"
              : undefined
          }
        />
      )}
      </div>

      {spec.series.length > 1 && (
        <ChartLegend
          items={legendItems}
          mask={mask?.series}
          hoveredIndex={legendIndexForSeries(hoveredSeries)}
          selectedIndex={legendIndexForSeries(isolatedSeries)}
          onHover={(displayIdx) =>
            setHoveredSeries(
              displayIdx == null ? null : (seriesDisplayOrder[displayIdx] ?? null),
            )
          }
          onSelect={(displayIdx) => {
            const seriesIdx = seriesDisplayOrder[displayIdx];
            if (seriesIdx != null) toggleSeries(seriesIdx);
          }}
        />
      )}
    </div>
  );
}
